import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  interval,
  startWith,
  switchMap,
  takeWhile,
  tap,
  catchError,
  throwError,
} from 'rxjs';
import { ReportDelivery, ReportsService, ReportDeliveryStatus } from 'ldt-data-deliveries-api';
import { AuthService } from 'src/app/auth/service/auth.service';

@Injectable({
  providedIn: 'root',
})
export class DownloadStatusService {
  private activeDownloadId = new BehaviorSubject<number | null>(null);
  private activeDownload = new BehaviorSubject<ReportDelivery | null>(null);
  activeDownload$ = this.activeDownload.asObservable();
  private pollingSubscription: Subscription | null = null;

  constructor(
    private authService: AuthService,
    private deliveryService: ReportsService
  ) {}

  showDownloadStatus(deliveryId: number): void {
    this.checkReportDelivery(deliveryId, true).subscribe();
  }

  clearDownloadStatus(): void {
    this.activeDownload.next(null);
  }

  getActiveDownload(): Observable<ReportDelivery | null> {
    return this.activeDownload$;
  }

  shouldPoll(delivery: ReportDelivery): boolean {
    return (
      delivery.status === ReportDeliveryStatus.InProgress ||
      delivery.status === ReportDeliveryStatus.Scheduled
    );
  }

  startPolling(deliveryId: number): void {
    this.stopPolling(); // clear first

    this.pollingSubscription = interval(5000)
      .pipe(
        startWith(0), // Emit immediately for first check
        switchMap(() =>
          this.deliveryService.getReportDelivery(this.authService.getSelectedOrgIdValue, deliveryId)
        ),
        takeWhile((delivery) => this.shouldPoll(delivery), true), // Keep polling while in_progress
        tap((delivery) => {
          this.activeDownload.next(delivery);
        })
      )
      .subscribe({
        complete: () => {
          // Polling finished (status no longer in_progress)
          this.pollingSubscription = null;
        },
        error: (err) => {
          console.error('Error polling delivery status:', err);
          this.pollingSubscription = null;
        },
      });
  }

  stopPolling(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = null;
    }
  }

  checkDeliveryStatus(deliveryId: number): void {
    this.startPolling(deliveryId);
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  /**
   * Fetches a report delivery by ID and optionally starts polling
   * @param deliveryId The ID of the report delivery to check
   * @param poll Whether to start polling if the status is in_progress (default: true)
   * @returns An Observable of the report delivery
   */
  checkReportDelivery(deliveryId: number, poll: boolean = true): Observable<ReportDelivery> {
    const orgId = this.authService.getSelectedOrgIdValue;

    // Create the initial request to get the report delivery
    return this.deliveryService.getReportDelivery(orgId, deliveryId).pipe(
      tap((delivery) => {
        if (this.shouldPoll(delivery) && poll) {
          this.startPolling(delivery.id);
        }

        this.activeDownload.next(delivery);
      }),
      catchError((error) => {
        console.error('Error fetching report delivery:', error);
        return throwError(() => error);
      })
    );
  }
}
